import React, { Fragment, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import classNames from 'classnames';

import { floatDaysLabel } from '../schedule/scheduleEntries';

import MilestoneDetailModal from './MilestoneDetailModal';

import { formatDateLong } from 'Helpers/dateFormat';
import { modalOptions } from 'Helpers/modalOptions';

import TableSortHeaderCell from 'CommonComponents/TableSortHeaderCell';

import ProjectRoadmapContext from 'Context/project_plan/ProjectRoadmapContext';

import useModal from 'Hooks/useModal';

import ChevronDownIcon from 'Assets/icons/chevron-down.svg';
import ChevronRightIcon from 'Assets/icons/chevron-right.svg';
import StarFilledIcon from 'Assets/icons/star-filled.svg';

const ROW_H = 40;
const HEADER_H = 40;
const OVERSCAN_ROWS = 10;
const SCROLL_PERSIST_MS = 300;

const GRID_COLUMNS = 'tw-grid tw-grid-cols-[minmax(0,34fr)_minmax(0,24fr)_minmax(0,14fr)_minmax(0,14fr)_minmax(0,14fr)]';
// First-level categories only hides the Sub-category column; the Milestone column takes its room.
const GRID_COLUMNS_NO_SUBCATEGORY = 'tw-grid tw-grid-cols-[minmax(0,58fr)_minmax(0,14fr)_minmax(0,14fr)_minmax(0,14fr)]';
// While the critical path is on, a Float column follows Status.
const GRID_COLUMNS_FLOAT = 'tw-grid tw-grid-cols-[minmax(0,30fr)_minmax(0,22fr)_minmax(0,13fr)_minmax(0,12fr)_minmax(0,13fr)_minmax(0,10fr)]';
const GRID_COLUMNS_FLOAT_NO_SUBCATEGORY = 'tw-grid tw-grid-cols-[minmax(0,52fr)_minmax(0,13fr)_minmax(0,12fr)_minmax(0,13fr)_minmax(0,10fr)]';

const gridColumnsFor = (showSubcategory, showFloat) => {
    if (showFloat) {
        return showSubcategory ? GRID_COLUMNS_FLOAT : GRID_COLUMNS_FLOAT_NO_SUBCATEGORY;
    }

    return showSubcategory ? GRID_COLUMNS : GRID_COLUMNS_NO_SUBCATEGORY;
};

const SORT_DATE = 'date';
const SORT_SUBCATEGORY_ASC = 'subcategory_asc';
const SORT_SUBCATEGORY_DESC = 'subcategory_desc';

// Each click on the Sub-category header moves to the next order.
const NEXT_SORT = { [SORT_DATE]: SORT_SUBCATEGORY_ASC, [SORT_SUBCATEGORY_ASC]: SORT_SUBCATEGORY_DESC, [SORT_SUBCATEGORY_DESC]: SORT_DATE };

const ARIA_SORT = { [SORT_DATE]: 'none', [SORT_SUBCATEGORY_ASC]: 'ascending', [SORT_SUBCATEGORY_DESC]: 'descending' };

const countsLabel = (counts) =>
    `${counts.milestones === 1 ? $t('1 milestone') : $t('{{count}} milestones', { count: counts.milestones })} · ${$t('{{count}} key', { count: counts.key })}`;

const byDate = (a, b) => (a.milestone.date === b.milestone.date ? a.milestone.id - b.milestone.id : a.milestone.date < b.milestone.date ? -1 : 1);

const groupName = (subcategory) => subcategory.trim().toLowerCase();

// Milestones directly on the category sort after every named sub-category, so Z–A puts them
// first. A plain string comparison, like ProjectRoadmapPdfLayoutService::listRows.
const bySubcategory = (a, b) => {
    if (a.subcategory === null || b.subcategory === null) {
        return (a.subcategory === null) - (b.subcategory === null);
    }

    const [nameA, nameB] = [groupName(a.subcategory), groupName(b.subcategory)];

    return nameA === nameB ? 0 : nameA < nameB ? -1 : 1;
};

const COMPARATORS = {
    [SORT_DATE]: byDate,
    [SORT_SUBCATEGORY_ASC]: (a, b) => bySubcategory(a, b) || byDate(a, b),
    [SORT_SUBCATEGORY_DESC]: (a, b) => bySubcategory(b, a) || byDate(a, b),
};

/**
 * Source -> Category rows, each category's milestones (its own and every sub-category's) in one
 * date order or grouped by sub-category, with the Sub-category column saying where each belongs.
 * Collapsing a source or a category hides every row beneath it.
 */
const buildListRows = (sources, collapsed, sort) => {
    const rows = [];

    sources.forEach((source) => {
        const sourceKey = `source:${source.key}`;
        const sourceCollapsed = !!collapsed[sourceKey];

        rows.push({ key: sourceKey, type: 'source', name: source.name, counts: source.counts, collapsed: sourceCollapsed });

        if (sourceCollapsed) {
            return;
        }

        source.categories.forEach((category) => {
            const categoryKey = `category:${category.id}`;
            const categoryCollapsed = !!collapsed[categoryKey];

            rows.push({ key: categoryKey, type: 'category', name: category.name, counts: category.counts, collapsed: categoryCollapsed });

            if (categoryCollapsed) {
                return;
            }

            const milestones = [
                ...category.milestones.map((milestone) => ({ milestone, subcategory: null })),
                ...category.subcategories.flatMap((subcategory) => subcategory.milestones.map((milestone) => ({ milestone, subcategory: subcategory.name }))),
            ].sort(COMPARATORS[sort] || byDate);

            milestones.forEach(({ milestone, subcategory }) => {
                rows.push({ key: `milestone:${milestone.id}`, type: 'milestone', milestone, subcategory });
            });
        });
    });

    return rows;
};

const RoadmapList = () => {
    const { data, settings, update, toggleCollapsed, registerPositionFlusher, milestoneUrl } = useContext(ProjectRoadmapContext);

    const scrollerRef = useRef(null);
    const [scrollTop, setScrollTop] = useState(0);
    const [viewportHeight, setViewportHeight] = useState(0);
    const [selectedMilestone, setSelectedMilestone] = useState(null);

    const milestoneModal = useModal({ options: { ...modalOptions(true), backdrop: 'dynamic' } });
    const { show: showMilestoneModal, hide: hideMilestoneModal } = milestoneModal;

    const showSubcategory = !settings.firstLevel;
    // With the Sub-category column hidden its sort gives way to date order; the stored value stays.
    const sort = showSubcategory ? settings.listSort : SORT_DATE;
    const showFloat = !!data.schedule;
    const gridColumns = gridColumnsFor(showSubcategory, showFloat);

    const rows = useMemo(() => buildListRows(data.tree.sources, settings.collapsed, sort), [data.tree.sources, settings.collapsed, sort]);

    const cycleSort = useCallback(() => update({ listSort: NEXT_SORT[settings.listSort] || SORT_SUBCATEGORY_ASC }), [update, settings.listSort]);

    useLayoutEffect(() => {
        const element = scrollerRef.current;
        const measure = () => setViewportHeight(element.clientHeight);

        measure();

        // The list keeps its own scroll position, apart from the calendar's.
        if (settings.listScrollTop) {
            element.scrollTop = settings.listScrollTop;
            setScrollTop(element.scrollTop);
        }

        const observer = new ResizeObserver(measure);
        observer.observe(element);

        return () => observer.disconnect();
        // Restores once, on mount.
    }, []);

    // ── Scroll persistence ──
    const persistTimerRef = useRef(null);
    const pendingScrollTopRef = useRef(null);

    const persistScrollTop = useCallback(() => {
        clearTimeout(persistTimerRef.current);
        persistTimerRef.current = null;

        if (pendingScrollTopRef.current !== null) {
            update({ listScrollTop: pendingScrollTopRef.current });
            pendingScrollTopRef.current = null;
        }
    }, [update]);

    const persistScrollTopRef = useRef(persistScrollTop);
    persistScrollTopRef.current = persistScrollTop;

    useEffect(() => registerPositionFlusher(() => persistScrollTopRef.current()), [registerPositionFlusher]);
    useEffect(() => () => persistScrollTopRef.current(), []);

    const handleScroll = useCallback(
        (event) => {
            const top = event.currentTarget.scrollTop;
            setScrollTop(top);
            pendingScrollTopRef.current = Math.round(top);
            clearTimeout(persistTimerRef.current);
            persistTimerRef.current = setTimeout(persistScrollTop, SCROLL_PERSIST_MS);
        },
        [persistScrollTop],
    );

    const openMilestone = useCallback(
        (milestone) => {
            setSelectedMilestone(milestone);
            showMilestoneModal();
        },
        [showMilestoneModal],
    );

    const first = Math.max(0, Math.floor(scrollTop / ROW_H) - OVERSCAN_ROWS);
    const last = Math.min(rows.length, Math.ceil((scrollTop + viewportHeight) / ROW_H) + OVERSCAN_ROWS);

    return (
        <Fragment>
            <div
                ref={scrollerRef}
                onScroll={handleScroll}
                className="tw-relative tw-overflow-auto tw-max-h-[70vh] tw-min-h-[240px] tw-border tw-border-charcoal-25 tw-rounded-lg tw-bg-white"
                role="table"
                aria-rowcount={rows.length}
            >
                <div
                    className={classNames(
                        gridColumns,
                        'tw-sticky tw-top-0 tw-z-10 tw-bg-blue-10 tw-border-b tw-border-charcoal-25 tw-text-0.5sm tw-font-semibold tw-text-charcoal',
                    )}
                    style={{ height: HEADER_H }}
                    role="row"
                >
                    <div role="columnheader" className="tw-flex tw-items-center tw-px-3">
                        {$t('Milestone')}
                    </div>
                    {showSubcategory && (
                        <div role="columnheader" aria-sort={ARIA_SORT[sort] || 'none'} className="tw-flex tw-items-center tw-px-3">
                            <button type="button" className="tw-flex tw-items-center tw-font-semibold" onClick={cycleSort}>
                                <TableSortHeaderCell asc={sort === SORT_SUBCATEGORY_ASC} desc={sort === SORT_SUBCATEGORY_DESC}>
                                    {$t('Sub-category')}
                                </TableSortHeaderCell>
                            </button>
                        </div>
                    )}
                    {[$t('Date'), $t('Type'), $t('Status'), ...(showFloat ? [$t('Float')] : [])].map((heading) => (
                        <div key={heading} role="columnheader" className="tw-flex tw-items-center tw-px-3">
                            {heading}
                        </div>
                    ))}
                </div>

                <div className="tw-relative" style={{ height: rows.length * ROW_H }}>
                    {rows.slice(first, last).map((row, index) => (
                        <ListRow
                            key={row.key}
                            row={row}
                            top={(first + index) * ROW_H}
                            gridColumns={gridColumns}
                            showSubcategory={showSubcategory}
                            showFloat={showFloat}
                            onToggle={toggleCollapsed}
                            onOpen={openMilestone}
                        />
                    ))}
                </div>
            </div>

            <MilestoneDetailModal modal={milestoneModal} milestone={selectedMilestone} detailsUrl={milestoneUrl} onClose={hideMilestoneModal} />
        </Fragment>
    );
};

// Coloured as the project plan's Float column: 0 red, near-critical amber.
const FloatCell = ({ float }) => (
    <div role="cell" className="tw-px-3 tw-whitespace-nowrap">
        <span className={classNames('inbud-numeric-text tw-font-medium', float === 0 ? 'tw-text-red' : 'tw-text-amber-700')}>{floatDaysLabel(float)}</span>
    </div>
);

const ListRow = ({ row, top, gridColumns, showSubcategory, showFloat, onToggle, onOpen }) => {
    const style = { top, height: ROW_H };

    if (row.type !== 'milestone') {
        const isSource = row.type === 'source';
        const Chevron = row.collapsed ? ChevronRightIcon : ChevronDownIcon;
        const content = (
            <Fragment>
                <Chevron className="tw-h-2 tw-w-2 tw-shrink-0" aria-hidden="true" />
                <span className="tw-truncate">{row.name}</span>
                <span className={classNames('tw-shrink-0 tw-text-2xs tw-font-normal', isSource ? 'tw-text-charcoal-25' : 'tw-text-charcoal-50')}>
                    · {countsLabel(row.counts)}
                </span>
            </Fragment>
        );
        const className = classNames('tw-absolute tw-inset-x-0 tw-flex tw-items-center tw-gap-2 tw-border-b tw-text-start tw-font-semibold', {
            'tw-bg-charcoal tw-text-white tw-border-charcoal tw-ps-3 tw-text-sm': isSource,
            'tw-bg-charcoal-10 tw-text-charcoal tw-border-grey tw-ps-6 tw-text-0.5sm': !isSource,
        });

        return (
            <button type="button" className={className} style={style} aria-expanded={!row.collapsed} onClick={() => onToggle(row.key)}>
                {content}
            </button>
        );
    }

    const { milestone } = row;

    return (
        <div
            className={classNames(gridColumns, 'tw-absolute tw-inset-x-0 tw-items-center tw-border-b tw-border-grey tw-text-0.5sm tw-text-charcoal')}
            style={style}
            role="row"
        >
            <div role="cell" className="tw-ps-10 tw-pe-3 tw-truncate">
                <button type="button" className="tw-truncate tw-max-w-full tw-text-start hover:tw-underline" onClick={() => onOpen(milestone)}>
                    {milestone.name}
                </button>
            </div>
            {showSubcategory && (
                <div role="cell" className="tw-px-3 tw-truncate tw-text-charcoal-75">
                    {row.subcategory || '—'}
                </div>
            )}
            <div role="cell" className="tw-px-3 tw-whitespace-nowrap">
                {formatDateLong(milestone.date)}
            </div>
            <div role="cell" className="tw-px-3">
                {milestone.key ? (
                    <span className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-bg-blue tw-px-2 tw-py-0.5 tw-text-2xs tw-font-semibold tw-text-white">
                        <StarFilledIcon className="tw-h-2.5 tw-w-2.5" aria-hidden="true" />
                        {$t('Key')}
                    </span>
                ) : (
                    $t('Milestone')
                )}
            </div>
            <div role="cell" className="tw-px-3">
                {milestone.complete ? (
                    <span className="tw-inline-flex tw-rounded-full tw-bg-green-25 tw-px-2 tw-py-0.5 tw-text-2xs tw-font-semibold tw-text-dark-green">
                        {$t('Complete')}
                    </span>
                ) : (
                    milestone.status_label
                )}
            </div>
            {showFloat && <FloatCell float={milestone.float} />}
        </div>
    );
};

export default RoadmapList;
